import { useEffect } from 'react';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider } from '../../../../elements/common';
import { ReadCustomControlData } from '../../../../elements/forms/ReadCustomControlData';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';

export function TocV1RequestRead(props: ICustomControlRequestReadProps) {
    useEffect(() => {
        if (props.request.status === RequestStatus.draft && props.request.created_at === props.request.updated_at) {
            props.editRequest();
        }
    }, []);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={ServiceType.consultation}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <ProfileImages request={props.request} />

                <ServiceConsumerSupervisorRead {...props} />

                <ReadCustomControlData
                    label="Vraag"
                    value={props.request.question}
                />

                <FormDivider />
                <ReadCustomControlData
                    label="Toelichting"
                    value={props.request.customControlData.toelichting}
                />

                {/* processes migrated from Txcs can contain diagnoses */}
                <Icd10SelectionsRead
                    diagnosis={props.request.diagnosis}
                    hideTitle
                    showWorkingDiagnosis
                    thesaurusTermDate={props.request.dateSent}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <RequestReadButtons
                {...props}
                removeProcess={async () => {
                    await props.removeProcess();
                    if (props.request.previousRequestId) {
                        await props.removeProcess(props.request.previousRequestId);
                    }
                }}
            />
        </ProcessContainer>
    );
}
